import UTM from './utm'
import { parseURL, parseUrlQuery } from './utils'

interface BrowserInfo {
  browser: string
  browser_version: string
}

interface OSInfo {
  os_name: string
  os_version: string
}

interface LatestData {
  $latest_referrer: string
  $latest_referrer_host: string
  $latest_search_keyword: string
  isLast: boolean
}

interface ClientData {
  platform: string
  os_name: string
  os_version: string
  browser: string
  browser_version: string
  device_model: string
  screen_width: number
  screen_height: number
  language: string
  referrer: string
  referrer_host: string
  utm: Record<string, unknown> | null
  latest_data: LatestData
  userAgent: string
}

class Client {
  appid: number
  domain: string
  cookie_expire: number
  userAgent: string
  appVersion: string
  utm: Record<string, unknown> | null = null

  constructor(app_id: number, domain: string, cookie_expire: number) {
    this.appid = app_id
    this.domain = domain
    this.cookie_expire = cookie_expire
    this.userAgent = window.navigator.userAgent
    this.appVersion = window.navigator.appVersion
  }

  init(): ClientData {
    const language = window.navigator.language
    const referrer = document.referrer
    const referrer_host = referrer ? parseURL(referrer).hostname : ''
    const platform = /Mobile|htc|mini|Android|iP(ad|od|hone)/.test(this.appVersion) ? 'wap' : 'web'

    // UTM 自动从 URL 解析和缓存
    this.utm = UTM() as Record<string, unknown> | null

    const browserInfo = this.getBrowserInfo()
    const osInfo = this.getOSInfo()

    return {
      browser: browserInfo.browser,
      browser_version: browserInfo.browser_version,
      platform,
      os_name: osInfo.os_name,
      os_version: osInfo.os_version,
      userAgent: this.userAgent,
      screen_width: window.screen?.width || 0,
      screen_height: window.screen?.height || 0,
      device_model: this.getDeviceModel(osInfo.os_name),
      language,
      referrer,
      referrer_host,
      utm: this.utm,
      latest_data: this.getLatestData(referrer, referrer_host),
    }
  }

  getLatestData(referrer: string, referrer_host: string): LatestData {
    let $latest_referrer = ''
    let $latest_referrer_host = ''
    let $latest_search_keyword = ''
    let isLast = false
    const hostname = location.hostname

    if (referrer && referrer_host && hostname !== referrer_host) {
      $latest_referrer = referrer
      $latest_referrer_host = referrer_host
      isLast = true
      const referQuery = parseUrlQuery(referrer)
      $latest_search_keyword = referQuery['keyword'] || ''
    }

    return { $latest_referrer, $latest_referrer_host, $latest_search_keyword, isLast }
  }

  getBrowserInfo(): BrowserInfo {
    const ua = this.userAgent

    // 匹配顺序很重要：特定标识 > 通用标识，iOS 专属 > 桌面端/Android
    // 很多浏览器 UA 都包含 "Safari" 和 "Chrome"，必须先匹配更具体的标识
    const browserList: { name: string; regex: RegExp; versionRegex: RegExp }[] = [
      // 微信内置浏览器（iOS/Android 通用，必须最先匹配）
      { name: 'weixin', regex: /MicroMessenger/, versionRegex: /MicroMessenger\/([\d.]+)/ },
      // QQ 浏览器
      { name: 'qqbrowser', regex: /MQQBrowser|QQBrowser/, versionRegex: /(?:MQQ|QQ)Browser\/([\d.]+)/ },
      // iOS 专属标识（必须在桌面版之前匹配，iOS 第三方浏览器使用独有的 UA 标识）
      { name: 'Chrome', regex: /CriOS/, versionRegex: /CriOS\/([\d.]+)/ },
      { name: 'Firefox', regex: /FxiOS/, versionRegex: /FxiOS\/([\d.]+)/ },
      { name: 'Edge', regex: /EdgiOS/, versionRegex: /EdgiOS\/([\d.]+)/ },
      { name: 'Opera', regex: /OPiOS/, versionRegex: /OPiOS\/([\d.]+)/ },
      // 桌面端 / Android 浏览器
      { name: 'Edge', regex: /Edg|Edge/, versionRegex: /Edg(?:e)?\/([\d.]+)/ },
      { name: 'Microsoft Internet Explorer', regex: /MSIE/, versionRegex: /MSIE\s([\d.]+)/ },
      { name: 'Lark', regex: /Lark/, versionRegex: /Lark\/([\d.]+)/ },
      { name: 'sougoubrowser', regex: /MetaSr/, versionRegex: /MetaSr\s([\d.]+)/ },
      { name: 'Chrome', regex: /Chrome/, versionRegex: /Chrome\/([\d.]+)/ },
      { name: 'Firefox', regex: /Firefox/, versionRegex: /Firefox\/([\d.]+)/ },
      { name: 'Opera', regex: /Opera|OPR/, versionRegex: /(?:Opera|OPR)\/([\d.]+)/ },
      // Safari 放最后，因为几乎所有 iOS 浏览器 UA 都包含 "Safari"
      { name: 'Safari', regex: /Safari/, versionRegex: /Version\/([\d.]+)/ },
    ]

    for (const b of browserList) {
      if (b.regex.test(ua)) {
        const match = b.versionRegex.exec(ua)
        console.log('浏览器信息--->:', match)
        console.log('browser--->:', b.name)
        return { browser: b.name, browser_version: match?.[1] || 'unknown' }
      }
    }

    return { browser: 'unknown', browser_version: `${parseFloat(this.appVersion)}` }
  }

  getOSInfo(): OSInfo {
    let os_name = ''
    let os_version: string | number = ''
    const ua = this.userAgent

    const osList = [
      { name: 'Windows 10', regex: /(Windows 10.0|Windows NT 10.0|Windows NT 10.1)/ },
      { name: 'Windows 8.1', regex: /(Windows 8.1|Windows NT 6.3)/ },
      { name: 'Windows 8', regex: /(Windows 8|Windows NT 6.2)/ },
      { name: 'Windows 7', regex: /(Windows 7|Windows NT 6.1)/ },
      // HarmonyOS:必须放在 Android 之前,因鸿蒙 ArkWeb UA 通常也含 "Android" 兼容标识
      { name: 'HarmonyOS', regex: /(OpenHarmony|HarmonyOS|ArkWeb)/ },
      { name: 'Android', regex: /Android/ },
      { name: 'iOS', regex: /(iPhone|iPad|iPod)/ },
      { name: 'Mac OS X', regex: /Mac OS X/ },
      { name: 'Linux', regex: /(Linux|X11)/ },
      { name: 'Sun OS', regex: /SunOS/ },
      { name: 'Mac OS', regex: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
    ]

    for (const os of osList) {
      if (os.regex.test(ua)) {
        os_name = os.name
        if (os_name === 'Mac OS X' && this.isNewIpad()) os_name = 'iOS'
        break
      }
    }

    const getVersion = (reg: RegExp, target: string) => {
      const result = reg.exec(target)
      return result && result[1] ? result[1] : ''
    }

    const getAndroidVersion = (ua: string) => getVersion(/Android[\/ ]([\.\_\d]+)/, ua)

    switch (os_name) {
      case 'Mac OS X':
        os_version = getVersion(/Mac OS X ([\d_\.]+)/, ua)
        os_name = 'mac'
        break
      case 'iOS':
        if (this.isNewIpad()) {
          os_version = getVersion(/Mac OS X ([\d_\.]+)/, ua)
        } else {
          const match = /OS (\d+)_(\d+)_?(\d+)?/.exec(this.appVersion)
          os_version = match ? `${match[1]}.${match[2]}.${match[3] || 0}` : ''
        }
        break
      case 'Android':
        os_version = getAndroidVersion(ua)
        break
      case 'HarmonyOS':
        // 优先取 OpenHarmony / HarmonyOS 后的版本号,兜底按 Android 兼容版本提取
        os_version =
          getVersion(/(?:OpenHarmony|HarmonyOS)[\/ ]([\.\_\d]+)/, ua) || getAndroidVersion(ua)
        break
      case 'Windows 10':
      case 'Windows 8.1':
      case 'Windows 8':
      case 'Windows 7':
        os_version = getVersion(/Windows (.*)/, os_name)
        os_name = 'windows'
        break
    }

    return { os_name: os_name.toLowerCase(), os_version }
  }

  getDeviceModel(osName: string) {
    let model = ''
    try {
      const ua = navigator.userAgent
      if (osName === 'android' || osName === 'harmonyos') {
        // HarmonyOS ArkWeb UA 通常兼容 Android Build 段,沿用相同的机型提取方式
        const parts = ua.split(';')
        parts.forEach((item) => {
          if (item.includes('Build/')) {
            const buildParts = item.split('Build/')
            model = buildParts[0]?.trim() || ''
          }
        })
      } else if (osName === 'ios' || osName === 'mac' || osName === 'windows') {
        if (this.isNewIpad()) {
          model = 'iPad'
        } else {
          const temp = ua.replace('Mozilla/5.0 (', '')
          const parts = temp.split(';')
          model = parts[0]?.trim() || ''
        }
      }
    } catch {
      return model.trim()
    }
    return model.trim()
  }

  isNewIpad() {
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  }
}

export default Client
